/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Input, Select, Button, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text } from 'atoms';
import { searchVehiclesV2, transferVehicleV2, replaceVehicleV2 } from 'store/vehicleSliceV2';
import { searchVendorV2 } from 'store/vendorSliceV2';
import TransferVehicleModal from './TransferVehicleModal';
import ReplaceVehicleModal from './ReplaceVehicleModal';

const { Option } = Select;

const PageContainer = styled(Box)`
	padding: 24px;
	background-color: #f9fafb;
	min-height: 100vh;
`;

const ContentWrapper = styled(Box)`
	background: white;
	border-radius: 8px;
	padding: 24px;
	max-width: 1200px;
`;

const PageHeader = styled(Flex)`
	align-items: center;
	justify-content: space-between;
	margin-bottom: 24px;
`;

const BackButton = styled(Button)`
	display: flex;
	align-items: center;
	gap: 8px;
	border: none;
	box-shadow: none;
	padding: 4px 8px;
	height: auto;
	font-size: 24px;
	color: #1f2937;

	&:hover {
		color: #1e3a8a;
		background: transparent;
	}
`;

const HeaderLeft = styled(Flex)`
	align-items: center;
	gap: 16px;
`;

const PageTitle = styled(Text)`
	font-size: 24px;
	font-weight: 600;
	color: #1f2937;
	margin: 0;
`;

const PageSubtitle = styled(Text)`
	font-size: 14px;
	color: #6b7280;
	margin-top: 4px;
	display: block;
`;

const HeaderActions = styled(Flex)`
	gap: 12px;
`;

const TransferButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #1990ff;
	color: #1990ff;
	background: #f0f7ff;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		border-color: #1060cc !important;
		color: #1060cc !important;
		background: #e0f0ff !important;
	}
`;

const ReplaceButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #22c55e;
	color: #16a34a;
	background: #f0fff4;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover {
		border-color: #15803d !important;
		color: #15803d !important;
		background: #dcfce7 !important;
	}
`;

const EditButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	background-color: #1e3a8a;
	border-color: #1e3a8a;
	color: white;
	font-weight: 500;
	display: flex;
	align-items: center;
	gap: 8px;

	&:hover:not(:disabled) {
		background-color: #1e40af;
		border-color: #1e40af;
		color: white;
	}

	&:disabled {
		background-color: #9ca3af;
		border-color: #9ca3af;
		color: white;
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

const StyledForm = styled(Form)`
	.ant-form-item {
		margin-bottom: 20px;
	}

	.ant-form-item-label > label {
		font-weight: 500;
		color: #374151;
		font-size: 14px;
	}

	.ant-input,
	.ant-select-selector {
		height: 40px;
		border-radius: 6px;
		border: 1px solid #d1d5db;
		background-color: #e5e7eb;
	}

	.ant-select-selector {
		display: flex;
		align-items: center;
	}

	.ant-input:hover,
	.ant-select-selector:hover {
		border-color: #9ca3af;
	}

	.ant-input:focus,
	.ant-select-focused .ant-select-selector {
		border-color: #1e3a8a;
		box-shadow: 0 0 0 2px rgba(30, 58, 138, 0.1);
	}
`;

const FormGrid = styled(Box)`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	margin-bottom: 24px;
`;

const FooterActions = styled(Flex)`
	justify-content: flex-end;
	gap: 12px;
	margin-top: 32px;
	padding-top: 24px;
	border-top: 1px solid #e5e7eb;
`;

const CancelButton = styled(Button)`
	height: 40px;
	padding: 0 24px;
	border: 1px solid #d1d5db;
	color: #374151;
	background: white;
	font-weight: 500;

	&:hover {
		border-color: #9ca3af;
		color: #1f2937;
		background: white;
	}
`;

const SaveButton = styled(Button)`
	height: 40px;
	padding: 0 32px;
	background-color: ${(props) => (props.disabled ? '#9ca3af' : '#1e3a8a')};
	border-color: ${(props) => (props.disabled ? '#9ca3af' : '#1e3a8a')};
	color: white;
	font-weight: 500;

	&:hover:not(:disabled) {
		background-color: #1e40af;
		border-color: #1e40af;
		color: white;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
`;

export default function EditVehiclePageV2() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { id } = useParams();
	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [vehicleData, setVehicleData] = useState(null);
	const [transferModalVisible, setTransferModalVisible] = useState(false);
	const [replaceModalVisible, setReplaceModalVisible] = useState(false);
	const [vendorOptions, setVendorOptions] = useState([]);
	const [isEditing, setIsEditing] = useState(false);

	useEffect(() => {
		if (id) {
			fetchVehicleDetails();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		fetchVendors();
	}, []);

	const fetchVehicleDetails = async () => {
		setLoading(true);
		try {
			const response = await dispatch(
				searchVehiclesV2({
					filter_by: { id },
					pagination: { page: 1, per_page: 1 },
				})
			);

			if (response?.rows && response.rows.length > 0) {
				const vehicle = response.rows[0];
				setVehicleData(vehicle);

				// Populate form with vehicle data
				form.setFieldsValue({
					maker: vehicle.vehicle_info?.make || 'TATA',
					model: vehicle.vehicle_type_name || '',
					capacity: vehicle.capacity_config?.max_weight_kg || '',
					length: vehicle.capacity_config?.dimensions?.length_m || '',
					temperatureControlled: 'Yes',
					driverMode: 'Manual',
					fuelType: 'Diesel',
				});
			}
		} catch (error) {
			console.error('Error fetching vehicle details:', error);
			message.error('Failed to fetch vehicle details');
		} finally {
			setLoading(false);
		}
	};

	const fetchVendors = async () => {
		try {
			const response = await dispatch(
				searchVendorV2({
					search_type: 'vendor',
					pagination: { page: 1, per_page: 1000 },
				})
			);

			if (response?.rows) {
				const options = response.rows.map((vendor) => ({
					value: vendor.id,
					label: vendor.name,
				}));
				setVendorOptions(options);
			}
		} catch (error) {
			console.error('Error fetching vendors:', error);
		}
	};

	const handleBack = () => {
		navigate('/vehicles');
	};

	const handleTransfer = () => {
		if (!vehicleData) {
			message.error('Vehicle data not loaded');
			return;
		}
		setTransferModalVisible(true);
	};

	const handleTransferConfirm = async (transferData) => {
		try {
			// Call the transfer API
			const response = await dispatch(
				transferVehicleV2({
					req_data: {
						vehicle_id: vehicleData.id,
						to_vendor_id: transferData.to_vendor_id,
						reason: transferData.reason,
						last_working_date: transferData.last_working_date,
						start_date: transferData.start_date,
					},
				})
			);

			if (response && response.status === 'success') {
				setTransferModalVisible(false);
				// Navigate back to vehicle list after successful transfer request
				navigate('/vehicles');
				return true;
			} else if (response?.code === 'NOT_FOUND') {
				message.error('Vehicle or vendor not found');
			} else if (response?.code === 'INVALID_STATE') {
				message.error('Vehicle has active trips. Cannot transfer at this time.');
			} else if (response?.code === 'SAME_VENDOR') {
				message.error('Target vendor is the same as current vendor');
			} else {
				message.error(response?.message || 'Failed to create transfer request');
			}
			return false;
		} catch (error) {
			console.error('Error transferring vehicle:', error);
			message.error('An error occurred while creating transfer request');
			return false;
		}
	};

	const handleTransferModalClose = () => {
		setTransferModalVisible(false);
	};

	const handleReplace = () => {
		if (!vehicleData) {
			message.error('Vehicle data not loaded');
			return;
		}
		setReplaceModalVisible(true);
	};

	const handleReplaceConfirm = async (replaceData) => {
		try {
			const response = await dispatch(
				replaceVehicleV2({
					req_data: {
						old_vehicle_id: replaceData.old_vehicle_id,
						new_vehicle_id: replaceData.new_vehicle_id,
						vendor_id: replaceData.vendor_id,
						reason: replaceData.reason,
						replacement_date: replaceData.replacement_date,
					},
				})
			);

			if (response && response.status === 'success') {
				setReplaceModalVisible(false);
				navigate('/vehicles');
				return true;
			}
			if (response?.code === 'NOT_FOUND') {
				message.error('Vehicle or vendor not found');
			} else if (response?.code === 'INVALID_STATE') {
				message.error('Vehicle has active trips. Cannot replace at this time.');
			} else if (response?.code === 'SAME_VEHICLE') {
				message.error('New vehicle is the same as the current vehicle');
			} else {
				message.error(response?.message || 'Failed to create replacement request');
			}
			return false;
		} catch (error) {
			console.error('Error replacing vehicle:', error);
			message.error('An error occurred while creating replacement request');
			return false;
		}
	};

	const handleEdit = () => {
		setIsEditing(true);
	};

	const handleCancel = () => {
		if (isEditing) {
			// Reset form to original values
			setIsEditing(false);
			fetchVehicleDetails();
		} else {
			navigate('/vehicles');
		}
	};

	const handleSave = async () => {
		try {
			await form.validateFields();
			// TODO: Implement save functionality with API
			setIsEditing(false);
			message.success('Vehicle details updated successfully');
		} catch (error) {
			console.error('Validation error:', error);
		}
	};

	return (
		<PageContainer>
			<ContentWrapper>
				<PageHeader>
					<HeaderLeft>
						<BackButton onClick={handleBack} icon={<ArrowLeftOutlined />} />
						<Box>
							<PageTitle>Vehicles</PageTitle>
							<PageSubtitle>Manage Vehicles here</PageSubtitle>
						</Box>
					</HeaderLeft>
					<HeaderActions>
						<TransferButton onClick={handleTransfer}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8 1L4 5M8 1L12 5M8 1V11M8 15L4 11M8 15L12 11M8 15V5"
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Transfer
						</TransferButton>
						<ReplaceButton onClick={handleReplace}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
									stroke="#16a34a"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							Replace
						</ReplaceButton>
						<EditButton onClick={handleEdit} disabled={isEditing}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M11.333 2.00004C11.5081 1.82494 11.716 1.68605 11.9447 1.59129C12.1735 1.49653 12.4187 1.44775 12.6663 1.44775C12.914 1.44775 13.1592 1.49653 13.3879 1.59129C13.6167 1.68605 13.8246 1.82494 13.9997 2.00004C14.1748 2.17513 14.3137 2.383 14.4084 2.61178C14.5032 2.84055 14.552 3.08575 14.552 3.33337C14.552 3.58099 14.5032 3.82619 14.4084 4.05497C14.3137 4.28374 14.1748 4.49161 13.9997 4.66671L4.99967 13.6667L1.33301 14.6667L2.33301 11L11.333 2.00004Z"
									stroke="white"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
									fill="white"
								/>
							</svg>
							Edit
						</EditButton>
					</HeaderActions>
				</PageHeader>

				<StyledForm form={form} layout="vertical" loading={loading}>
					<FormGrid>
						<Form.Item
							name="maker"
							label="Maker/Manufacturer"
							rules={[{ required: true, message: 'Please select maker' }]}
						>
							<Select placeholder="Select" disabled={!isEditing}>
								<Option value="TATA">TATA</Option>
								<Option value="Ashok Leyland">Ashok Leyland</Option>
								<Option value="Mahindra">Mahindra</Option>
							</Select>
						</Form.Item>

						<Form.Item
							name="model"
							label="Model Name"
							rules={[{ required: true, message: 'Please select model' }]}
						>
							<Select placeholder="Select" disabled={!isEditing}>
								<Option value="Ace">Ace</Option>
								<Option value="407">407</Option>
								<Option value="Dost">Dost</Option>
							</Select>
						</Form.Item>

						<Form.Item
							name="capacity"
							label="Capacity (in tonnes)"
							rules={[{ required: true, message: 'Please enter capacity' }]}
						>
							<Input placeholder="Enter capacity" disabled={!isEditing} />
						</Form.Item>

						<Form.Item
							name="length"
							label="Length (in feet)"
							rules={[{ required: true, message: 'Please enter length' }]}
						>
							<Input placeholder="Enter length" disabled={!isEditing} />
						</Form.Item>

						<Form.Item
							name="temperatureControlled"
							label="Temperature Controlled"
							rules={[{ required: true, message: 'Please select option' }]}
						>
							<Select placeholder="Select" disabled={!isEditing}>
								<Option value="Yes">Yes</Option>
								<Option value="No">No</Option>
							</Select>
						</Form.Item>

						<Form.Item
							name="driverMode"
							label="Driver Mode"
							rules={[{ required: true, message: 'Please select driver mode' }]}
						>
							<Select placeholder="Select" disabled={!isEditing}>
								<Option value="Manual">Manual</Option>
								<Option value="Automatic">Automatic</Option>
							</Select>
						</Form.Item>

						<Form.Item
							name="fuelType"
							label="Fuel Type"
							rules={[{ required: true, message: 'Please select fuel type' }]}
						>
							<Select placeholder="Select" disabled={!isEditing}>
								<Option value="Diesel">Diesel</Option>
								<Option value="Petrol">Petrol</Option>
								<Option value="CNG">CNG</Option>
								<Option value="Electric">Electric</Option>
							</Select>
						</Form.Item>
					</FormGrid>

					<FooterActions>
						<CancelButton onClick={handleCancel}>
							{isEditing ? 'Cancel' : 'Back'}
						</CancelButton>
						<SaveButton onClick={handleSave} disabled={!isEditing}>
							Save Changes
						</SaveButton>
					</FooterActions>
				</StyledForm>
			</ContentWrapper>

			<TransferVehicleModal
				visible={transferModalVisible}
				vehicleData={vehicleData}
				vendorOptions={vendorOptions.filter((v) => v.value !== vehicleData?.vendor_id)}
				onConfirm={handleTransferConfirm}
				onClose={handleTransferModalClose}
			/>

			<ReplaceVehicleModal
				visible={replaceModalVisible}
				vehicleData={vehicleData}
				onConfirm={handleReplaceConfirm}
				onClose={() => setReplaceModalVisible(false)}
			/>
		</PageContainer>
	);
}
